<template>
  <section class="section-line">
    <!-- Filter grid: row 1 -->
    <div class="filter-grid">
      <!-- Upload Date (JST) -->
      <div class="filter-field">
        <div class="filter-field__label-row">
          <span class="filter-label">UPLOAD DATE (JST)</span>
          <label class="filter-radio">
            <input
              type="radio"
              value="specific"
              v-model="dateMode"
            />
            <span>Specific Date</span>
          </label>
          <label class="filter-radio">
            <input
              type="radio"
              value="range"
              v-model="dateMode"
            />
            <span>Date Range</span>
          </label>
        </div>

        <!-- Specific Date -->
        <div v-if="dateMode === 'specific'" class="filter-input-wrap">
          <input
            type="date"
            lang="en"
            class="filter-date-input"
            v-model="specificDate"
          />
          <button
            v-if="specificDate"
            type="button"
            class="filter-clear-btn"
            @click="specificDate = ''"
            aria-label="Clear date"
          >✕</button>
        </div>

        <!-- Date Range -->
        <div v-else class="filter-date-range">
          <div
            class="filter-input-wrap"
            :class="{ 'filter-input-wrap--error': isInvalidDateRange }"
          >
            <input
              type="date"
              lang="en"
              class="filter-date-input"
              :class="{ 'filter-date-input--error': isInvalidDateRange }"
              placeholder="Start date"
              v-model="startDate"
            />
            <button
              v-if="startDate"
              type="button"
              class="filter-clear-btn"
              @click="startDate = ''"
              aria-label="Clear start date"
            >✕</button>
          </div>
          <span class="filter-date-range__sep">–</span>
          <div
            class="filter-input-wrap"
            :class="{ 'filter-input-wrap--error': isInvalidDateRange }"
          >
            <input
              type="date"
              lang="en"
              class="filter-date-input"
              :class="{ 'filter-date-input--error': isInvalidDateRange }"
              placeholder="End date"
              v-model="endDate"
            />
            <button
              v-if="endDate"
              type="button"
              class="filter-clear-btn"
              @click="endDate = ''"
              aria-label="Clear end date"
            >✕</button>
          </div>
        </div>
      </div>

      <!-- DISTRIBUTOR CODE -->
      <div class="filter-field">
        <label class="filter-label">DISTRIBUTOR CODE <span class="filter-label__hint">(Prefix Match)</span></label>
        <div class="filter-input-wrap">
          <span class="filter-icon" aria-hidden="true">🔍</span>
          <input
            type="text"
            class="filter-text-input"
            placeholder="Search distributor code"
            v-model="dealerCode"
          />
          <button
            v-if="dealerCode"
            type="button"
            class="filter-clear-btn"
            @click="dealerCode = ''"
            aria-label="Clear distributor code"
          >✕</button>
        </div>
      </div>
    </div>

    <!-- Filter grid: row 2 -->
    <div class="filter-grid filter-grid--mt">
      <!-- Model Name -->
      <div class="filter-field">
        <label class="filter-label">MODEL NAME <span class="filter-label__hint">(Partial Match)</span></label>
        <div class="filter-input-wrap">
          <span class="filter-icon" aria-hidden="true">🔍</span>
          <input
            type="text"
            class="filter-text-input"
            placeholder="Search model name"
            v-model="modelName"
          />
          <button
            v-if="modelName"
            type="button"
            class="filter-clear-btn"
            @click="modelName = ''"
            aria-label="Clear model name"
          >✕</button>
        </div>
      </div>

      <!-- VIN -->
      <div class="filter-field">
        <label class="filter-label">VIN <span class="filter-label__hint">(Prefix Match)</span></label>
        <div class="filter-input-wrap">
          <span class="filter-icon" aria-hidden="true">🔍</span>
          <input
            type="text"
            class="filter-text-input"
            placeholder="Search VIN"
            v-model="vin"
          />
          <button
            v-if="vin"
            type="button"
            class="filter-clear-btn"
            @click="vin = ''"
            aria-label="Clear VIN"
          >✕</button>
        </div>
      </div>
    </div>

    <!-- Actions row -->
    <div class="filter-actions">
      <button
        type="button"
        class="btn btn-reset"
        :disabled="!hasSearchConditions || isLoading"
        @click="onResetClick"
      >
        ↺ Reset
      </button>
      <button
        type="button"
        class="btn btn-indigo"
        :disabled="isLoading || isInvalidDateRange"
        @click="onSearchClick"
      >
        Search
      </button>
    </div>
  </section>
</template>

<script>
import { computed, ref } from "vue";
import { jstDateToUtcYmdHms } from "../utils/dateUtils";

// Fallback date boundaries used when only one end of a range is specified.
// These values represent the practical min/max for the API's date filter.
const DATE_RANGE_MIN = "1900-01-01";
const DATE_RANGE_MAX = "9999-12-31";

export default {
  name: "AdminSearchFilter",
  props: {
    isLoading: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["search", "reset"],
  expose: ["clearAll"],
  setup(props, { emit }) {
    // ---- Filter state ----
    const dealerCode = ref("");
    const modelName = ref("");
    const vin = ref("");
    const dateMode = ref("specific"); // 'specific' | 'range'
    const specificDate = ref("");     // YYYY-MM-DD
    const startDate = ref("");        // YYYY-MM-DD
    const endDate = ref("");          // YYYY-MM-DD

    /**
     * Returns true if at least one condition relevant to the current dateMode is filled.
     * Ignores date fields that are not active in the current mode.
     */
    const hasSearchConditions = computed(() => {
      const hasDateCondition =
        dateMode.value === "specific"
          ? !!specificDate.value
          : !!(startDate.value || endDate.value);

      return !!(dealerCode.value || modelName.value || vin.value || hasDateCondition);
    });

    /** range モードで start > end の場合 true */
    const isInvalidDateRange = computed(() => {
      if (dateMode.value !== "range") return false;
      if (!startDate.value || !endDate.value) return false;
      return startDate.value > endDate.value;
    });

    /**
     * 現在のフィルター条件から API リクエスト用の filters オブジェクトを生成する。
     * 空の条件は省略する。
     */
    const buildFilters = () => {
      const filters = {};

      if (dealerCode.value) {
        filters.dealerCodePrefix = dealerCode.value;
      }
      if (modelName.value) {
        filters.modelNameContains = modelName.value;
      }
      if (vin.value) {
        filters.vinPrefix = vin.value;
      }

      if (dateMode.value === "specific" && specificDate.value) {
        filters.registerDate = {
          start: jstDateToUtcYmdHms(specificDate.value, "start"),
          end: jstDateToUtcYmdHms(specificDate.value, "end"),
        };
      } else if (dateMode.value === "range") {
        if (startDate.value || endDate.value) {
          filters.registerDate = {
            start: jstDateToUtcYmdHms(startDate.value || DATE_RANGE_MIN, "start"),
            end: jstDateToUtcYmdHms(endDate.value || DATE_RANGE_MAX, "end"),
          };
        }
      }

      return Object.keys(filters).length > 0 ? filters : undefined;
    };

    const onSearchClick = () => {
      if (isInvalidDateRange.value) return;
      emit("search", buildFilters());
    };

    const onResetClick = () => {
      emit("reset");
    };

    /**
     * 親コンポーネント（AdminPage）から呼び出して全フィールドをクリアする。
     */
    const clearAll = () => {
      dealerCode.value = "";
      modelName.value = "";
      vin.value = "";
      specificDate.value = "";
      startDate.value = "";
      endDate.value = "";
      dateMode.value = "specific";
    };

    return {
      dealerCode,
      modelName,
      vin,
      dateMode,
      specificDate,
      startDate,
      endDate,
      hasSearchConditions,
      isInvalidDateRange,
      onSearchClick,
      onResetClick,
      clearAll,
    };
  },
};
</script>

<style scoped>
.section-line {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px;
  margin: 0 0 16px;
}

/* ---- Filter grid ---- */
.filter-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.filter-grid--mt {
  margin-top: 16px;
}

@media (max-width: 640px) {
  .filter-grid {
    grid-template-columns: 1fr;
  }
}

.filter-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.filter-field__label-row {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: 12px;
  justify-content: flex-start;
  text-align: left;
}

.filter-label {
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  white-space: nowrap;
  text-align: left;
  width: auto;
  flex: 0 0 auto;
}

.filter-label__hint {
  font-weight: 400;
  color: #6b7280;
}

.filter-radio {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 13px;
  color: #374151;
  cursor: pointer;
  user-select: none;
}

.filter-radio input[type="radio"] {
  accent-color: #4f46e5;
  cursor: pointer;
}

/* Input wrapper */
.filter-input-wrap {
  display: flex;
  align-items: center;
  min-height: 38px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 0 10px;
  background: #ffffff;
  gap: 6px;
  transition: border-color 0.15s, box-shadow 0.15s;
}

.filter-input-wrap:focus-within {
  border-color: #4f46e5;
  box-shadow: 0 0 0 2px rgba(79, 70, 229, 0.15);
}

.filter-input-wrap--error {
  border-color: #dc2626;
}

.filter-input-wrap--error:focus-within {
  border-color: #dc2626;
  box-shadow: 0 0 0 2px rgba(220, 38, 38, 0.15);
}

.filter-icon {
  font-size: 13px;
  flex-shrink: 0;
  color: #6b7280;
}

.filter-text-input,
.filter-date-input {
  flex: 1 1 auto;
  border: none;
  outline: none;
  font-size: 13px;
  color: #111827;
  background: transparent;
  min-width: 0;
}

.filter-date-input {
  cursor: pointer;
}

.filter-date-input--error {
  color: #dc2626;
}

.filter-clear-btn {
  appearance: none;
  border: none;
  background: transparent;
  color: #9ca3af;
  font-size: 12px;
  cursor: pointer;
  padding: 0 2px;
  line-height: 1;
  flex-shrink: 0;
}

.filter-clear-btn:hover {
  color: #374151;
}

/* Date range layout */
.filter-date-range {
  display: flex;
  align-items: center;
  gap: 8px;
}

.filter-date-range .filter-input-wrap {
  flex: 1 1 0;
}

.filter-date-range__sep {
  font-size: 14px;
  color: #6b7280;
  flex-shrink: 0;
}

/* Actions row */
.filter-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 16px;
  padding-top: 14px;
  border-top: 1px solid #e5e7eb;
}

/* ---- Buttons ---- */
.btn {
  appearance: none;
  border: 1px solid transparent;
  border-radius: 6px;
  padding: 8px 14px;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.1;
  cursor: pointer;
  user-select: none;
}

.btn:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.btn-indigo {
  background: #4f46e5;
  border-color: #4f46e5;
  color: #fff;
}

.btn-indigo:hover:not(:disabled) {
  background: #4338ca;
  border-color: #4338ca;
}

.btn-indigo:focus {
  outline: none;
}

.btn-reset {
  background: #4b5563;
  border-color: #4b5563;
  color: #fff;
}

.btn-reset:hover:not(:disabled) {
  background: #374151;
  border-color: #374151;
}

.btn-reset:focus {
  outline: none;
}
</style>
